import { useState, useEffect } from 'react';
import './ModernDatePicker.css';

const ModernDatePicker = ({ selected, onChange, minDate, maxDate, className = '' }) => {
  const [currentDate, setCurrentDate] = useState(selected || new Date());
  const [monthDays, setMonthDays] = useState([]);
  const [nextMonthDays, setNextMonthDays] = useState([]);

  useEffect(() => {
    if (selected) {
      setCurrentDate(selected);
    }
  }, [selected]);

  useEffect(() => {
    generateCalendars(currentDate);
  }, [currentDate]);

  const generateDaysForMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const daysArray = [];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      daysArray.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      daysArray.push(new Date(year, month, day));
    }

    return daysArray;
  };

  const generateCalendars = (date) => {
    const days = generateDaysForMonth(date);
    const nextDate = new Date(date.getFullYear(), date.getMonth() + 1);
    const nextDays = generateDaysForMonth(nextDate);

    setMonthDays(days);
    setNextMonthDays(nextDays);
  };

  const previousMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isDateDisabled = (date) => {
    if (!date) return false;
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (minDate) {
      const compareMin = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      if (compareDate < compareMin) return true;
    }
    
    if (maxDate) {
      const compareMax = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      if (compareDate > compareMax) return true;
    }
    
    return false;
  };

  const isDateSelected = (date) => {
    if (!selected) return false;
    return date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const handleDayClick = (e, date) => {
    e.stopPropagation();
    if (!isDateDisabled(date)) {
      onChange(date);
    }
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const nextMonthName = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const handleCalendarClick = (e) => {
    e.stopPropagation();
  };

  const MonthCalendar = ({ month, year, days, label }) => (
    <div className="month-column">
      <div className="calendar-month-title">
        {label}
      </div>
      <div className="modern-calendar-weekdays">
        {dayNames.map((day) => (
          <div key={day} className="modern-calendar-weekday">
            {day}
          </div>
        ))}
      </div>
      <div className="modern-calendar-days">
        {days.map((date, index) => (
          <button
            key={index}
            type="button"
            className={`modern-calendar-day ${date === null ? 'modern-calendar-day--empty' : ''
              } ${date && isDateDisabled(date) ? 'modern-calendar-day--disabled' : ''
              } ${date && isDateSelected(date) ? 'modern-calendar-day--selected' : ''
              } ${date && isToday(date) ? 'modern-calendar-day--today' : ''
              }`}
            onClick={(e) => date && handleDayClick(e, date)}
            disabled={date && isDateDisabled(date)}
          >
            {date ? date.getDate() : ''}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`modern-date-picker ${className}`} onClick={handleCalendarClick}>
      <button
        type="button"
        className="modern-calendar-nav-btn modern-calendar-nav-prev"
        onClick={previousMonth}
        aria-label="Previous month"
      >
        ←
      </button>

      <div className="calendar-months-container">
        <MonthCalendar
          month={currentDate.getMonth()}
          year={currentDate.getFullYear()}
          days={monthDays}
          label={monthName}
        />
        <MonthCalendar
          month={(currentDate.getMonth() + 1) % 12}
          year={currentDate.getMonth() === 11 ? currentDate.getFullYear() + 1 : currentDate.getFullYear()}
          days={nextMonthDays}
          label={nextMonthName}
        />
      </div>

      <button
        type="button"
        className="modern-calendar-nav-btn modern-calendar-nav-next"
        onClick={nextMonth}
        aria-label="Next month"
      >
        →
      </button>
    </div>
  );
};

export default ModernDatePicker;
